import {
	FaCheck, FaEdit,
	FaExclamationTriangle, FaHeart,
	FaHeartbeat,
	FaLock, FaPlus, FaSave,
	FaSignInAlt, FaSignOutAlt,
	FaSkull, FaTimes, FaTrash,
	FaUsers,
} from "react-icons/fa";
import {StatusMember} from "../../Provider/useSquadInterfaces";
import { Styles } from "./Styles";
import useSquad from "../../Provider/useSquad";
import useGlobalSettings from "../../Provider/useGlobalSettings";
import React, {useEffect, useMemo, useState} from "react";
import {IoIosCog} from "react-icons/io";
import {statusLabels} from "./Squad";
import {Elements} from "../../Styles/Elements";


const statusTone: Record<StatusMember, "alive" | "injured" | "dead"> = {
	[StatusMember.alive]: "alive",
	[StatusMember.injured]: "injured",
	[StatusMember.dead]: "dead",
};


export const SquadCard = ( { squadID, mySquad }: { squadID?: string, mySquad?: boolean } ) => {

	const { user } = useGlobalSettings()
	const { squads, Actions } = useSquad()

	const squad = squads.find( squad => squad.id === squadID )
	const [objectiveDrafts, setObjectiveDrafts] = useState<Record<string, string>>({});
	const [isEditingSquad, setIsEditingSquad] = useState(false);
	const [editSquadName, setEditSquadName] = useState("");
	const [editSquadCallSign, setEditSquadCallSign] = useState("");

	const [expandMembers, setExpandMembers] = useState<boolean>(false);
	const [expandGoals, setExpandGoals] = useState<boolean>(true);

	const [editMode, setEditMode] = useState(false);

	const activeMember = useMemo(
		() => squad?.members.find(member => member.id === user.uuid),
		[squad, user.uuid]
	);


	useEffect(() => {
		if (!squad)
			return;

		setEditSquadName(squad.name);
		setEditSquadCallSign(squad.callSign);
	}, [squad?.id, squad?.name, squad?.callSign]);

	if( ! squad )
		return null;

	const teamMembers = squad.members;
	const alive = teamMembers.filter(member => member.status === StatusMember.alive).length;
	const injured = teamMembers.filter(member => member.status === StatusMember.injured).length;
	const dead = teamMembers.filter(member => member.status === StatusMember.dead).length;
	const totalMembers = teamMembers.length;
	const safeTotalMembers = totalMembers || 1;
	const survivalProgress = Math.round((alive / safeTotalMembers) * 100);
	const completedObjectives = squad.objectives.filter(objective => objective.completed).length;
	const isOwnSquad = !! squad.members.find(member => member.id === user.uuid );
	const supportRequested = squad.specialActions
		.find( action => action.name === "emergencySupportRequired" )
		?.active;

	console.log( "supportRequested", squad );


	const handles = {

		joinSquad: (squadId: string) => {
			Actions.squads.join( squadId, { id: user.uuid ?? "", name: user.name ?? "-" } );
			setTimeout( () =>
				document.getElementById("mySquad")?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				}), 1000 );
		},

		leaveSquad: () => {
			Actions.squads.leave( user.uuid ?? "" );
		},


		deleteSquad: (squadId: string) => {
			Actions.squads.delete( squadId );
		},

		startEditSquad: () => {
			setEditSquadName(squad.name);
			setEditSquadCallSign(squad.callSign);
			setIsEditingSquad(true);
		},
		cancelEditSquad: () => {
			setEditSquadName(squad.name);
			setEditSquadCallSign(squad.callSign);
			setIsEditingSquad(false);
		},

		saveEditSquad: () => {
			const name = editSquadName.trim();
			const callSign = editSquadCallSign.trim();

			if (!name || !callSign)
				return;

			Actions.squads.update(squad.id, name, callSign);
			setIsEditingSquad(false);
		},

		toggleSupport: (squadId: string) => {
			Actions.squads.toggleSupport( squadId );
		},

		addObjective: (squadId: string) => {
			const text = objectiveDrafts[squadId]?.trim();

			if (!text) {
				return;
			}

			Actions.squads.addObjective(squadId, text);

			setObjectiveDrafts(prev => ({
				...prev,
				[squadId]: "",
			}));
		},
		deleteObjective: (squadId: string, objectiveName: string) => {
			if( ! squad || squad.id !== squadId )
				return;

			Actions.squads.deleteObjective(squadId, objectiveName);
		},

		toggleObjective: (squadId: string, objectiveName: string) => {
			if( ! squad || squad.id !== squadId )
				return;

			Actions.squads.toggleObjective(squadId, objectiveName);
		},

		toggleExpandMembers: () => {
			setExpandMembers(prev => !prev);
		},
		toggleExpandGoals: () => {
			setExpandGoals(prev => !prev);
		},


		updateOwnStatus: (status: StatusMember) => {
			if( ! isOwnSquad ){
				return;
			}

			Actions.squads.updateMemberStatus( user.uuid ?? "", status );
		},

		toggleEditSquad: () => {
			setEditMode(prev => !prev);
		}
	};

	return (
		<Styles.SquadCard
			key={squad.id}
			$supportRequested={ !! supportRequested }
			$isOwnSquad={isOwnSquad}
			id={ mySquad ? "mySquad" : squad.id }
		>
			<Styles.SquadTop>
				<div>
					{isEditingSquad ? (
						<Styles.SquadEditForm>
							<Styles.Input
								placeholder="Regiment"
								value={editSquadName}
								onChange={event => setEditSquadName(event.target.value)}
								onKeyDown={event => {
									if (event.key === "Enter") {
										handles.saveEditSquad();
									}

									if (event.key === "Escape") {
										handles.cancelEditSquad();
									}
								}}
							/>

							<Styles.Input
								placeholder="Rufzeichen"
								value={editSquadCallSign}
								onChange={event => setEditSquadCallSign(event.target.value)}
								onKeyDown={event => {
									if (event.key === "Enter") {
										handles.saveEditSquad();
									}

									if (event.key === "Escape") {
										handles.cancelEditSquad();
									}
								}}
							/>
						</Styles.SquadEditForm>
					) : (
						<>
							<Styles.SquadName>{squad.name}</Styles.SquadName>
							<Styles.CallSign>{squad.callSign}</Styles.CallSign>
						</>
					)}
				</div>

				<div style={{display: "grid", gap: 8, justifyItems: "end"}}>
					{isOwnSquad && (
						<Styles.Badge $success>Dein Squad</Styles.Badge>
					)}
				</div>
			</Styles.SquadTop>

			<Styles.StatusGrid>
				<Elements.MiniPanel value={ alive.toString() } label={"Lebend"} Icon={ <FaHeart /> } valueColor={"green"}
							 tiny={ true }
							 $myStatus={ isOwnSquad && activeMember?.status === StatusMember.alive }
							 onClick={ () => handles.updateOwnStatus(StatusMember.alive) }
					/>

				<Elements.MiniPanel value={ injured.toString() } label={"Verletzt"} Icon={ <FaHeartbeat /> } valueColor={"yellow"}
				             tiny={ true }
				             $myStatus={ isOwnSquad && activeMember?.status === StatusMember.injured }
				             onClick={ () => handles.updateOwnStatus(StatusMember.injured) }
				/>

				<Elements.MiniPanel value={ dead.toString() } label={"Gefallen"} Icon={ <FaSkull /> } valueColor={"red"}
				             tiny={ true }
				             $myStatus={ isOwnSquad && activeMember?.status === StatusMember.dead }
				             onClick={ () => handles.updateOwnStatus(StatusMember.dead) }
				/>
			</Styles.StatusGrid>

			<Styles.ProgressOuter title={`${survivalProgress}% einsatzfähig`}>
				<Styles.ProgressInner $progress={survivalProgress}/>
			</Styles.ProgressOuter>

			<Styles.SectionTitle onClick={ handles.toggleExpandMembers }>
				Mitglieder <FaUsers/> {teamMembers.length}
			</Styles.SectionTitle>

			{ expandMembers &&
				( teamMembers.length === 0 ? (
					<Styles.LockedNotice>Keine Spieler in diesem Squad.</Styles.LockedNotice>
				) : (
					<Styles.MemberList>
						{teamMembers.map(member => (
							<Styles.MemberChip
								key={member.id}
								$status={statusTone[member.status]}
								$active={member.id === user.uuid}
							>
								{member.name} · {statusLabels[member.status]}
							</Styles.MemberChip>
						))}
					</Styles.MemberList>
				)
			)}

			<Styles.SectionTitle onClick={ handles.toggleExpandGoals }>
				Ziele {completedObjectives}/{squad.objectives.length}
			</Styles.SectionTitle>

			<Styles.ObjectiveList>
				{squad.objectives
					.filter( objective => expandGoals || isOwnSquad )
					.map(objective => (
					<Styles.Objective
						key={objective.name}
						$done={objective.completed}
						$locked={!isOwnSquad}
						onClick={() => handles.toggleObjective(squad.id, objective.name)}
					>
						<FaCheck/>
						<span>{objective.name}</span>
						{isOwnSquad && editMode ? (
							<Styles.ObjectiveDeleteButton
								type="button"
								title="Ziel löschen"
								onClick={event => {
									event.stopPropagation();
									handles.deleteObjective(squad.id, objective.name);
								}}
							>
								<FaTrash/>
							</Styles.ObjectiveDeleteButton>
						) : (
							<FaLock/>
						)}
					</Styles.Objective>
				))}
			</Styles.ObjectiveList>

			{!isOwnSquad && (
				<Styles.LockedNotice>
					<FaLock/>
					Ziele können nur von Mitgliedern dieses Squads abgehakt werden.
				</Styles.LockedNotice>
			)}

			{ isOwnSquad && editMode && (
				<Styles.AddObjective>
					<Styles.Input
						placeholder="Neues Ziel definieren"
						value={objectiveDrafts[squad.id] ?? ""}
						onChange={event =>
							setObjectiveDrafts(prev => ({
								...prev,
								[squad.id]: event.target.value,
							}))
						}
						onKeyDown={event => {
							if (event.key === "Enter") {
								handles.addObjective(squad.id);
							}
						}}
					/>

					<Styles.Button
						$variant="ghost"
						onClick={() => handles.addObjective(squad.id)}
					>
						<FaPlus/>
					</Styles.Button>
				</Styles.AddObjective>
			)}

			{ isOwnSquad && (<Styles.SectionTitle onClick={ handles.toggleEditSquad }>
					Konfigurieren <IoIosCog />
					{ ! editMode && <div style={{height: "20px"}} /> }
				</Styles.SectionTitle>
			)}

			{ isOwnSquad && editMode && (<>
				<Styles.Actions>
					{isOwnSquad && !isEditingSquad && (
						<Styles.DataPadButton onClick={ handles.startEditSquad } icon={<FaEdit />} variant={"secondary"}>
							Squad bearbeiten
						</Styles.DataPadButton>
					)}

					{isOwnSquad && isEditingSquad && (
						<Styles.SquadEditActions>
							<Styles.DataPadButton onClick={ handles.saveEditSquad } icon={<FaSave />} variant={"primary"}>
								Speichern
							</Styles.DataPadButton>
							<Styles.DataPadButton onClick={ handles.cancelEditSquad } icon={<FaTimes />} variant={"secondary"}>
								Abbrechen
							</Styles.DataPadButton>
						</Styles.SquadEditActions>
					)}

					{isOwnSquad && (
						<Styles.DataPadButton onClick={() => {
							if (window.confirm(`Squad "${squad.name}" wirklich löschen?`)) {
								handles.deleteSquad(squad.id);
							}
						}} icon={<FaTrash />} variant={"secondary"}>
							Löschen
						</Styles.DataPadButton>
					)}
				</Styles.Actions>
				<div style={{height: "20px"}} />
			</>
			)}

			<Styles.Actions>
				{isOwnSquad ? (
					<Styles.DataPadButton onClick={ handles.leaveSquad } icon={<FaSignOutAlt />} variant={"secondary"}>
						Verlassen
					</Styles.DataPadButton>
				) : (
					<Styles.DataPadButton onClick={ () => handles.joinSquad( squad.id ) } icon={<FaSignInAlt />} variant={"primary"}>
						Beitreten
					</Styles.DataPadButton>
				)}

				<Styles.DataPadButton onClick={ () => handles.toggleSupport(squad.id) } icon={<FaExclamationTriangle />} variant={"secondary"}>
					{ supportRequested ? "Notfall" : "Status ändern" }
				</Styles.DataPadButton>
			</Styles.Actions>
		</Styles.SquadCard>
	);
}